import { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { useLifeCheck } from '@/hooks/useLifeCheck';
import { supabase } from '@/integrations/supabase/client';
import { readScopedLocalJson, removeScopedLocalJson, writeScopedLocalJson } from '@/lib/offline-core';
import {
  buildEmptyLifeCheckAnswers,
  buildLifeCheckShareText,
  describeLifeCheckImpactChange,
  formatLifeCheckImpactRange,
  isLifeCheckComplete,
  LIFE_CHECK_HOUSING_OPTIONS,
  LIFE_CHECK_INCOME_OPTIONS,
  LIFE_CHECK_RESPONSIBILITY_OPTIONS,
  LIFE_CHECK_SUPPORT_OPTIONS,
  LIFE_CHECK_WORK_OPTIONS,
} from '@/lib/life-check';
import {
  dismissLifeCheckPrompt,
  dismissLifeCheckResurfacePrompt,
} from '@/lib/lifeCheckPrompt';
import type {
  LifeCheckAnswers,
  LifeCheckRecord,
  LifeCheckResponsibility,
  LifeCheckSectionReport,
  LifeCheckSupportType,
} from '@/types';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppFieldGroup,
  AppLoadingState,
  AppPage,
  AppPageIntro,
  AppText,
  AppTextArea,
  buildLifeCheckShareCardData,
  buildLifeCheckShareCardSvg,
} from '../../ui-foundation';
import { resolveAdultingSectionPath } from '../../ui-foundation/core';

const LIFE_CHECK_DRAFT_SCOPE = 'life-check-draft';

function mapLifeCheckRow(row: {
  id: string;
  answers: unknown;
  summary: unknown;
  created_at: string;
  updated_at: string;
}): LifeCheckRecord {
  return {
    id: row.id,
    answers: row.answers as LifeCheckAnswers,
    summary: row.summary as LifeCheckRecord['summary'],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function StepEyebrow({ children }: { children: string }) {
  return (
    <AppText
      fontSize="$xs"
      fontWeight="700"
      color="$colorMuted"
      className="uppercase tracking-[0.08em]"
    >
      {children}
    </AppText>
  );
}

function ChoiceButton({
  title,
  subtitle,
  selected,
  onPress,
}: {
  title: string;
  subtitle?: string;
  selected: boolean;
  onPress: () => void;
}) {
  return (
    <AppButton
      type="button"
      tone={selected ? 'primary' : 'outline'}
      size="lg"
      onPress={onPress}
      justifyContent="flex-start"
      height="auto"
      py="$md"
      width="100%"
    >
      <div className="text-left">
        <AppText
          fontSize="$sm"
          fontWeight="700"
          color={selected ? '$primaryForeground' : '$color'}
        >
          {title}
        </AppText>
        {subtitle ? (
          <AppText
            fontSize="$xs"
            color={selected ? '$primaryForeground' : '$colorMuted'}
            className="mt-1"
          >
            {subtitle}
          </AppText>
        ) : null}
      </div>
    </AppButton>
  );
}

function buildFallbackSectionReports(record: LifeCheckRecord): LifeCheckSectionReport[] {
  if (record.summary.sectionReports?.length) {
    return record.summary.sectionReports;
  }

  return (record.summary.priorities || []).map((priority) => ({
    section: priority.section,
    label: priority.label,
    emoji: priority.emoji,
    path: priority.path,
    flags: [priority.reason],
    nextStep: `Open ${priority.label} and take the next useful step there.`,
    impactMin: 0,
    impactMax: 0,
  }));
}

function LifeCheckSummaryCard({
  record,
  previousRecord,
}: {
  record: LifeCheckRecord;
  previousRecord: LifeCheckRecord | null;
}) {
  const navigate = useNavigate();
  const sectionReports = buildFallbackSectionReports(record);
  const changeLabel = describeLifeCheckImpactChange(record.summary, previousRecord?.summary ?? null);

  const handleShare = async () => {
    const text = buildLifeCheckShareText(record.summary);
    const cardSvg = buildLifeCheckShareCardSvg(record.summary);
    const file = typeof File === 'function'
      ? new File([cardSvg], 'adulting-life-check-card.svg', { type: 'image/svg+xml' })
      : null;

    try {
      if (
        typeof navigator !== 'undefined'
        && typeof navigator.share === 'function'
        && file
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] })
      ) {
        await navigator.share({
          title: 'Adulting Life Check',
          text,
          files: [file],
        });
        return;
      }

      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share({
          title: 'Adulting Life Check',
          text,
        });
        return;
      }

      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        const blob = new Blob([cardSvg], { type: 'image/svg+xml;charset=utf-8' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'adulting-life-check-card.svg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
        toast.success('Life Check card downloaded and summary copied.');
        return;
      }

      throw new Error('Share is not available on this device.');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      toast.error('Could not open the share sheet right now.');
    }
  };

  return (
    <AppCard>
      <AppCardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <StepEyebrow>Latest Life Check</StepEyebrow>
            <AppText fontSize="$lg" fontWeight="700" className="mt-1">
              Your Life Check report
            </AppText>
          </div>
          <AppText fontSize="$xs" color="$colorMuted">
            {formatDistanceToNow(new Date(record.createdAt), { addSuffix: true })}
          </AppText>
        </div>
        <AppText fontSize="$sm" fontWeight="700" className="mt-3">
          Estimated annual impact to check: {formatLifeCheckImpactRange(record.summary)}
        </AppText>
        <AppText color="$colorMuted" className="mt-2">
          {record.summary.headline}
        </AppText>
        {changeLabel ? (
          <AppText fontSize="$xs" color="$colorMuted" className="mt-2">
            {changeLabel}
          </AppText>
        ) : null}
      </AppCardHeader>

      <AppCardBody>
        <AppCard backgroundColor="$background">
          <AppCardBody>
            <StepEyebrow>Share image card</StepEyebrow>
            <AppText fontSize="$lg" fontWeight="700" className="mt-1">
              {buildLifeCheckShareCardData(record.summary).impactLabel}
            </AppText>
            <AppText color="$colorMuted" className="mt-2">
              {buildLifeCheckShareCardData(record.summary).findings.join(' · ')}
            </AppText>
          </AppCardBody>
        </AppCard>

        <AppButtonRow>
          <AppButton type="button" tone="outline" onPress={handleShare}>
            Share image card
          </AppButton>
          <AppButton type="button" tone="outline" onPress={() => navigate('/life-check')}>
            Run again
          </AppButton>
        </AppButtonRow>

        <div className="mt-4 space-y-3">
          {sectionReports.map((section) => (
            <AppButton
              key={section.section}
              type="button"
              tone="outline"
              size="lg"
              onPress={() => navigate(section.path)}
              justifyContent="flex-start"
              height="auto"
              py="$md"
              width="100%"
            >
              <div className="min-w-0 text-left">
                <AppText fontSize="$sm" fontWeight="700">
                  {section.emoji} {section.label}
                </AppText>
                <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                  {section.flags.join(' ')}
                </AppText>
                <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                  Next step: {section.nextStep}
                </AppText>
              </div>
            </AppButton>
          ))}
        </div>
      </AppCardBody>
    </AppCard>
  );
}

function LifeCheckHistoryCard({ records }: { records: LifeCheckRecord[] }) {
  const olderChecks = records.slice(1, 5);

  if (olderChecks.length === 0) {
    return null;
  }

  return (
    <AppCard>
      <AppCardHeader>
        <StepEyebrow>Saved results</StepEyebrow>
        <AppText fontSize="$lg" fontWeight="700" className="mt-1">
          Recent Life Checks
        </AppText>
        <AppText color="$colorMuted" className="mt-2">
          Adulting keeps the latest checks here so you can see whether things feel heavier or lighter over time.
        </AppText>
      </AppCardHeader>
      <AppCardBody>
        <div className="space-y-3">
          {olderChecks.map((record, index) => (
            <AppCard key={record.id} backgroundColor="$background">
              <AppCardBody>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <AppText fontSize="$sm" fontWeight="700">
                      {formatDistanceToNow(new Date(record.createdAt), { addSuffix: true })}
                    </AppText>
                    <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                      Estimated annual impact to check: {formatLifeCheckImpactRange(record.summary)}
                    </AppText>
                    <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                      {record.summary.headline}
                    </AppText>
                    {describeLifeCheckImpactChange(
                      record.summary,
                      olderChecks[index + 1]?.summary ?? null,
                    ) ? (
                      <AppText fontSize="$xs" color="$colorMuted" className="mt-1">
                        {describeLifeCheckImpactChange(
                          record.summary,
                          olderChecks[index + 1]?.summary ?? null,
                        )}
                      </AppText>
                    ) : null}
                  </div>
                </div>
              </AppCardBody>
            </AppCard>
          ))}
        </div>
      </AppCardBody>
    </AppCard>
  );
}

export default function LifeCheckPage() {
  const { data, user } = useApp();
  const { latestLifeCheck, loading, saveLifeCheck } = useLifeCheck();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [answers, setAnswers] = useState<LifeCheckAnswers>(buildEmptyLifeCheckAnswers);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<LifeCheckRecord[]>([]);
  const [draftRestored, setDraftRestored] = useState(false);
  const promptEntry = searchParams.get('entry');
  const isIgnitionEntry = promptEntry === 'ignition';
  const isResurfaceEntry = promptEntry === 'resurface';
  const canSubmit = isLifeCheckComplete(answers);
  const hasChildren = data.profile.children.some((child) => child.name.trim().length > 0);
  const skipPath = resolveAdultingSectionPath({
    preferred: localStorage.getItem('adulting_default_section'),
    lastVisited: localStorage.getItem('adulting_last_section'),
    context: {
      workType: data.profile.workType,
      dependantTypes: data.profile.dependantTypes,
      hasChildren,
    },
  });
  const showIgnitionIntro = isIgnitionEntry && !latestLifeCheck;
  const showResurfaceIntro = isResurfaceEntry && !!latestLifeCheck;

  useEffect(() => {
    if (!user) {
      setDraftRestored(false);
      return;
    }

    const savedDraft = readScopedLocalJson<LifeCheckAnswers>(LIFE_CHECK_DRAFT_SCOPE, user.id);
    if (!savedDraft) {
      setDraftRestored(false);
      return;
    }

    setAnswers((current) => {
      const untouched = JSON.stringify(current) === JSON.stringify(buildEmptyLifeCheckAnswers());
      return untouched ? { ...buildEmptyLifeCheckAnswers(), ...savedDraft } : current;
    });
    setDraftRestored(true);
  }, [user]);

  useEffect(() => {
    let active = true;

    const loadHistory = async () => {
      if (!user) {
        if (active) {
          setHistory([]);
        }
        return;
      }

      const { data: rows, error } = await supabase
        .from('life_checks')
        .select('id, answers, summary, created_at, updated_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (!active) {
        return;
      }

      if (error) {
        console.error('Failed to load Life Check history:', error);
        return;
      }

      setHistory((rows || []).map(mapLifeCheckRow));
    };

    void loadHistory();
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (!latestLifeCheck) {
      return;
    }

    setHistory((current) => {
      const withoutLatest = current.filter((record) => record.id !== latestLifeCheck.id);
      return [latestLifeCheck, ...withoutLatest].slice(0, 5);
    });
  }, [latestLifeCheck]);

  const currentRecord = history[0] || latestLifeCheck || null;
  const previousRecord = history.length > 1 ? history[1] : null;
  const hasStartedDraft = Boolean(
    answers.workSituation
    || answers.housingSituation
    || answers.monthlyIncomeBand
    || answers.responsibilities.length
    || answers.supportTypes.length
    || answers.note?.trim(),
  );

  useEffect(() => {
    if (!user) {
      return;
    }

    if (hasStartedDraft && !canSubmit) {
      writeScopedLocalJson(LIFE_CHECK_DRAFT_SCOPE, user.id, answers);
      return;
    }

    removeScopedLocalJson(LIFE_CHECK_DRAFT_SCOPE, user.id);
  }, [answers, canSubmit, hasStartedDraft, user]);

  const toggleResponsibility = (value: LifeCheckResponsibility) => {
    setAnswers((current) => {
      if (value === 'just-me') {
        return {
          ...current,
          responsibilities: current.responsibilities.includes('just-me') ? [] : ['just-me'],
        };
      }

      const next = current.responsibilities.includes(value)
        ? current.responsibilities.filter((item) => item !== value)
        : [...current.responsibilities.filter((item) => item !== 'just-me'), value];

      return {
        ...current,
        responsibilities: next,
      };
    });
  };

  const toggleSupport = (value: LifeCheckSupportType) => {
    setAnswers((current) => {
      if (value === 'none') {
        return {
          ...current,
          supportTypes: current.supportTypes.includes('none') ? [] : ['none'],
        };
      }

      const next = current.supportTypes.includes(value)
        ? current.supportTypes.filter((item) => item !== value)
        : [...current.supportTypes.filter((item) => item !== 'none'), value];

      return {
        ...current,
        supportTypes: next,
      };
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Answer all five Life Check questions first.');
      return;
    }

    setSaving(true);
    try {
      const saved = await saveLifeCheck(answers);
      setHistory((current) => [saved, ...current.filter((record) => record.id !== saved.id)].slice(0, 5));
      removeScopedLocalJson(LIFE_CHECK_DRAFT_SCOPE, user?.id || '');
      setDraftRestored(false);
      toast.success('Life Check saved.');
    } catch (error) {
      console.error(error);
      toast.error('Could not save your Life Check.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (isIgnitionEntry) {
      dismissLifeCheckPrompt(user?.id);
    }
    if (isResurfaceEntry) {
      dismissLifeCheckResurfacePrompt(user?.id);
    }
    navigate(skipPath, { replace: true });
  };

  const responsibilitySummary = useMemo(
    () => new Set(answers.responsibilities),
    [answers.responsibilities],
  );
  const supportSummary = useMemo(
    () => new Set(answers.supportTypes),
    [answers.supportTypes],
  );

  return (
    <AppPage className="animate-fade-in">
      <AppPageIntro
        title="Life Check"
        subtitle={
          showIgnitionIntro
            ? 'Start here. This is the two-minute check that tells Adulting what matters in your life right now.'
            : showResurfaceIntro
              ? 'Your last Life Check is getting stale. Re-run it so Adulting reflects what matters now, not what mattered a few weeks ago.'
              : 'A quick five-question reset to work out what matters now, then turn it into a practical report.'
        }
      />

      {loading ? (
        <AppLoadingState
          title="Loading your latest Life Check"
          body="Pulling in your most recent check-in so you can update it without losing context."
        />
      ) : (
        <div className="space-y-6">
          {showIgnitionIntro ? (
            <AppCard>
              <AppCardHeader>
                <StepEyebrow>Start here</StepEyebrow>
                <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                  Let Adulting work out what matters first
                </AppText>
                <AppText color="$colorMuted" className="mt-3">
                  Five quick questions give Adulting a useful starting point across money, home, health, people, and work, so you do not have to guess where to begin.
                </AppText>
              </AppCardHeader>
              <AppCardBody>
                <AppButton tone="outline" onPress={handleSkip}>
                  Skip for now
                </AppButton>
              </AppCardBody>
            </AppCard>
          ) : showResurfaceIntro && latestLifeCheck ? (
            <AppCard>
              <AppCardHeader>
                <StepEyebrow>Check back in</StepEyebrow>
                <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                  Your last Life Check is from {formatDistanceToNow(new Date(latestLifeCheck.createdAt), { addSuffix: true })}
                </AppText>
                <AppText color="$colorMuted" className="mt-3">
                  Re-running it is the quickest way to update Adulting when the pressure has shifted. Skip it for now if the old report is still good enough.
                </AppText>
              </AppCardHeader>
              <AppCardBody>
                <AppButton tone="outline" onPress={handleSkip}>
                  Skip for now
                </AppButton>
              </AppCardBody>
            </AppCard>
          ) : null}

          {draftRestored ? (
            <AppCard backgroundColor="$mutedBackground">
              <AppCardBody>
                <StepEyebrow>Draft restored</StepEyebrow>
                <AppText fontSize="$sm" color="$colorMuted" className="mt-2">
                  Adulting brought back your unfinished Life Check so you can carry on instead of starting again.
                </AppText>
              </AppCardBody>
            </AppCard>
          ) : null}

          {currentRecord ? (
            <LifeCheckSummaryCard record={currentRecord} previousRecord={previousRecord} />
          ) : null}

          <AppCard>
            <AppCardHeader>
              <StepEyebrow>Question 1</StepEyebrow>
              <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                How do you work?
              </AppText>
              <AppText color="$colorMuted" className="mt-2">
                Pick the work setup that best matches life right now.
              </AppText>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {LIFE_CHECK_WORK_OPTIONS.map((option) => (
                  <ChoiceButton
                    key={option.value}
                    title={option.label}
                    subtitle={option.hint}
                    selected={answers.workSituation === option.value}
                    onPress={() => setAnswers((current) => ({ ...current, workSituation: option.value }))}
                  />
                ))}
              </div>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <StepEyebrow>Question 2</StepEyebrow>
              <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                What is your housing situation?
              </AppText>
              <AppText color="$colorMuted" className="mt-2">
                This helps Adulting point the report at the right home admin.
              </AppText>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {LIFE_CHECK_HOUSING_OPTIONS.map((option) => (
                  <ChoiceButton
                    key={option.value}
                    title={option.label}
                    subtitle={option.hint}
                    selected={answers.housingSituation === option.value}
                    onPress={() => setAnswers((current) => ({ ...current, housingSituation: option.value }))}
                  />
                ))}
              </div>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <StepEyebrow>Question 3</StepEyebrow>
              <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                Who are you responsible for?
              </AppText>
              <AppText color="$colorMuted" className="mt-2">
                Pick everything that applies. If it is just you, choose that on its own.
              </AppText>
            </AppCardHeader>
            <AppCardBody>
              <AppButtonRow>
                {LIFE_CHECK_RESPONSIBILITY_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    type="button"
                    tone={responsibilitySummary.has(option.value) ? 'primary' : 'outline'}
                    size="sm"
                    onPress={() => toggleResponsibility(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-3">
                {responsibilitySummary.size > 0
                  ? Array.from(responsibilitySummary).map((value) => LIFE_CHECK_RESPONSIBILITY_OPTIONS.find((option) => option.value === value)?.hint).filter(Boolean).join(' ')
                  : 'No selection yet.'}
              </AppText>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <StepEyebrow>Question 4</StepEyebrow>
              <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                Roughly what comes in each month?
              </AppText>
              <AppText color="$colorMuted" className="mt-2">
                This keeps the report realistic rather than assuming the same budget for everyone.
              </AppText>
            </AppCardHeader>
            <AppCardBody>
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {LIFE_CHECK_INCOME_OPTIONS.map((option) => (
                  <ChoiceButton
                    key={option.value}
                    title={option.label}
                    subtitle={option.hint}
                    selected={answers.monthlyIncomeBand === option.value}
                    onPress={() => setAnswers((current) => ({ ...current, monthlyIncomeBand: option.value }))}
                  />
                ))}
              </div>
            </AppCardBody>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <StepEyebrow>Question 5</StepEyebrow>
              <AppText fontSize="$lg" fontWeight="700" className="mt-1">
                What support are you already getting?
              </AppText>
              <AppText color="$colorMuted" className="mt-2">
                Pick everything that is already in place. If none of it is in place, choose the first option on its own.
              </AppText>
            </AppCardHeader>
            <AppCardBody>
              <AppButtonRow>
                {LIFE_CHECK_SUPPORT_OPTIONS.map((option) => (
                  <AppButton
                    key={option.value}
                    type="button"
                    tone={supportSummary.has(option.value) ? 'primary' : 'outline'}
                    size="sm"
                    onPress={() => toggleSupport(option.value)}
                  >
                    {option.label}
                  </AppButton>
                ))}
              </AppButtonRow>
              <AppText fontSize="$xs" color="$colorMuted" className="mt-3">
                {supportSummary.size > 0
                  ? Array.from(supportSummary).map((value) => LIFE_CHECK_SUPPORT_OPTIONS.find((option) => option.value === value)?.hint).filter(Boolean).join(' ')
                  : 'No selection yet.'}
              </AppText>

              <AppFieldGroup
                label="Anything else we should keep in view? Optional."
                helper="One line is enough. This is just to capture context you do not want to lose."
              >
                <AppTextArea
                  value={answers.note || ''}
                  onChange={(event) => setAnswers((current) => ({ ...current, note: event.target.value }))}
                  placeholder="One line is enough. This is just to capture context you do not want to lose."
                  rows={4}
                />
              </AppFieldGroup>
            </AppCardBody>
          </AppCard>

          <AppButtonRow>
            <AppButton
              type="button"
              onPress={handleSubmit}
              disabled={!canSubmit || saving}
              flex={1}
            >
              {saving ? 'Saving…' : 'Save Life Check'}
            </AppButton>
            <AppButton
              type="button"
              tone="outline"
              onPress={() => {
                setAnswers(buildEmptyLifeCheckAnswers());
                if (user?.id) {
                  removeScopedLocalJson(LIFE_CHECK_DRAFT_SCOPE, user.id);
                }
                setDraftRestored(false);
              }}
            >
              Reset
            </AppButton>
            {showIgnitionIntro || showResurfaceIntro ? (
              <AppButton
                type="button"
                tone="outline"
                onPress={handleSkip}
              >
                Skip for now
              </AppButton>
            ) : null}
          </AppButtonRow>

          <LifeCheckHistoryCard records={history} />
        </div>
      )}
    </AppPage>
  );
}
